// Aviso al entrar (dirección): pedidos ya hechos que se han quedado cortos por
// eventos nuevos o cambiados en los días pedidos (usuario, 05-oct-2026). Mismo
// estilo que la validación comercial. Solo sale si hay cortos sin revisar (los
// sobrantes no abren el popup) y una vez por sesión del navegador: "Más tarde"
// lo cierra hasta la siguiente sesión. "Ver la diferencia" lleva a Compras y
// Almacén > Compra de la semana con el rango del aviso, el proveedor filtrado
// y "Solo lo que falta".
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { listaCompraV2API } from '../api/api';
import { proveedoresConCortos, textoResumenAvisos, urlVerDiferencia } from './compras/listaCompraUtils';

const CLAVE_SESION = 'aviso_pedidos_cortos_visto';
const ROLES_AVISO = ['direccion', 'admin'];

const leerSesion = () => {
  try {
    return sessionStorage.getItem(CLAVE_SESION) === '1';
  } catch (e) {
    return false;
  }
};

const guardarSesion = () => {
  try {
    sessionStorage.setItem(CLAVE_SESION, '1');
  } catch (e) {
    // sin sessionStorage: el aviso puede volver a salir
  }
};

const AvisoPedidosCortos = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const puedeVer = ROLES_AVISO.includes(user?.rol);
  const [avisos, setAvisos] = useState(null);
  const [abierto, setAbierto] = useState(false);

  const cargar = useCallback(async () => {
    if (!puedeVer || leerSesion()) return;
    try {
      const { data } = await listaCompraV2API.avisosPedidos({ dias: 14 });
      if (proveedoresConCortos(data).length > 0 && !leerSesion()) {
        setAvisos(data);
        setAbierto(true);
        guardarSesion();
      }
    } catch (e) {
      // silencioso: no molestar si falla la carga
    }
  }, [puedeVer]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  if (!puedeVer || !abierto || !avisos) return null;
  const proveedores = proveedoresConCortos(avisos);

  const ver = (proveedorId) => {
    setAbierto(false);
    navigate(urlVerDiferencia(avisos, proveedorId));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4" data-testid="aviso-pedidos-cortos">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-amber-50">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h2 className="text-lg font-bold text-gray-900">Pedidos que se han quedado cortos</h2>
          </div>
          <button onClick={() => setAbierto(false)} className="text-gray-400 hover:text-gray-700" aria-label="Cerrar">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 text-sm text-gray-700 bg-gray-50 border-b border-gray-100" data-testid="aviso-pedidos-cortos-resumen">
          {textoResumenAvisos(proveedores)}. Pide solo lo que falta sobre lo ya pedido.
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {proveedores.map(p => (
            <div key={p.proveedor_id} className="border border-gray-200 rounded-lg p-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-gray-900">
                  {p.proveedor_nombre} · {p.cortos.length} {p.cortos.length === 1 ? 'ingrediente' : 'ingredientes'}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {p.cortos.slice(0, 4).map(c => `${c.nombre}: falta ${c.texto_falta || c.falta}`).join(' · ')}
                  {p.cortos.length > 4 ? ` · y ${p.cortos.length - 4} más` : ''}
                </p>
                {(p.eventos_nuevos || []).length > 0 && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    Eventos nuevos: {p.eventos_nuevos.map(e => e.numero).join(', ')}
                  </p>
                )}
              </div>
              <button
                onClick={() => ver(p.proveedor_id)}
                className="shrink-0 px-3 py-1.5 text-sm rounded-lg bg-duetto-primary text-white hover:opacity-90"
                data-testid={`aviso-pedidos-cortos-ver-${p.proveedor_id}`}
              >
                Ver la diferencia
              </button>
            </div>
          ))}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
          <button
            onClick={() => setAbierto(false)}
            className="px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
            data-testid="aviso-pedidos-cortos-mas-tarde"
          >
            Más tarde
          </button>
        </div>
      </div>
    </div>
  );
};

export default AvisoPedidosCortos;
